<template>
  <div>

    <b-container fluid="xxl" class="mt-2">
      <b-row class="mt-4">
        <b-col cols="4" class="p-0">
          <PageTitle v-bind:title="'Senses'" />
        </b-col>
        <b-col cols="8" class="text-right" v-if="!config.editable">
          <DictionarySelectPI class="mb-3" />
        </b-col>
      </b-row>
      <b-row class="mt-4">
        <b-col>
          <div v-if="$store.state.user.settings.isDictionaryFiltered && config.editable" class="mb-3 text-orange">
            <b-icon icon="exclamation-circle-fill" aria-hidden="true"></b-icon>
            Entries in this table are filtered by personal preference for dictionary set in user settings
            ({{ $store.getters.getDictionariesByIds($store.state.user.settings.dictionaries).map(dictionary => dictionary.acronym).join(', ') }})
          </div>
          <b-button v-if="config.editable" variant="primary" size="sm" :disabled="!$store.getters.checkRole('legam.sem.sense.add')" class="float-right" v-b-modal.modal-new-sense>
            <b-icon icon="plus-circle-fill" aria-hidden="true"></b-icon>
            Add new sense
          </b-button>
          <b-modal v-if="config.editable" id="modal-new-sense" title="New sense" ok-title="Save" @ok="saveSense" size="lg">
            <SenseNewEdit />
          </b-modal>
          <!-- <b-modal id="modal-edit-sense" title="Edit sense" ok-title="Save" @ok="saveSense" size="lg">
            <SenseNewEdit :sense="sense" />
          </b-modal> -->

          <LegamDataTables
            v-if="config.editable"
            v-bind="$data"
            @click="editSense"
            @selectChanged="selectChanged"
            :additionalData="tableAdditionalData"
          />
          <LegamDataTablesClientSide
            v-else
            v-bind="$data"
            @click="openDetails"
            @selectChanged="selectChanged"
            ref="PITable"
            :additionalDataFilter="tableAdditionalDataFilter"
          />
        </b-col>
      </b-row>
    </b-container>

    <div class="merge-float" v-if="selected.length && config.editable">
      <b-badge href="#" variant="info" class="mr-1" v-for="sense in selected" :key="sense.id" @click="removeSelectedSense(sense)">
          <span v-html="cropSense(sense)"></span>
        <b-icon icon="trash" aria-hidden="true" class="ml-1"></b-icon>
      </b-badge>
      <span class="btn-legam-xxs btn-legam-xs btn-legam-blue" v-if="selected.length > 1" v-b-modal.modal-merge-senses>
        <b-icon icon="plus-circle-fill" aria-hidden="true"></b-icon>
        Merge senses
      </span>
      <span class="btn-legam-xxs btn-legam-xs btn-legam-blue" v-if="selected.length > 0"  v-b-modal.modal-manipulate-categories>
        <b-icon icon="plus-circle-fill" aria-hidden="true"></b-icon>
        Manipulate Categories
      </span>
      <span class="btn-legam-xxs btn-legam-xs" @click="selected = []">
        <b-icon icon="x" aria-hidden="true"></b-icon>
      </span>

      <b-modal id="modal-merge-senses" title="Merge senses" ok-title="Merge" @ok="mergeSenses" size="md">
        <b-badge href="#" variant="info" class="mr-1" v-for="sense in selected" :key="'a' + sense.id" @click="removeSelectedSense(sense)">
          <span v-html="cropSense(sense, 25)"></span>
          <b-icon icon="trash" aria-hidden="true" class="ml-1"></b-icon>
        </b-badge>
        <b-form-group
          id="main-sense-grp"
          label="Main sense"
          label-for="main-sense"
          class="mt-3"
          description="This sense will keep all its data"
        >
          <b-form-select
            v-model="mainSense"
            :options="selected.map(sense => { return {
              'text': cropSense(sense, 38),
              'value': sense.id}
            })"
          ></b-form-select>
        </b-form-group>
        <b-form-checkbox
          v-if="config.editable"
          v-model="moveCategories"
          name="check-button"
          switch
          class="mt-4 mb-3"
          size="sm"
        >
          Move Categories
        </b-form-checkbox>
      </b-modal>

      <b-modal
        id="modal-manipulate-categories"
        title="Manipulate categories"
        ok-title="Apply Changes"
        @shown="computeSelectedCategories"
        @ok="manipulateCategories"
        size="md"
      >
        <b-badge
          href="#"
          variant="info"
          class="mr-1 d-block"
          v-for="category in sortedSelectedCategories()"
          :key="category[0]"
          @click="removeSelectedCategory(category)"
        >
          <span v-html="category[2] + ' (' + category[1] + ')'"></span>
          <b-icon icon="trash" aria-hidden="true" class="ml-1"></b-icon>
        </b-badge>

        <hr>

        <CategorySelect
          v-if="config.editable"
          @select="selectCategory"
          :cleanOnSelect="true"
        >Add Category</CategorySelect>

        <hr>

        All selected senses will receive the above selected categories 
      </b-modal>

    </div>
  </div>
</template>

<style scoped>
  * >>> .tdAction {
    width: 100px;
  }

  .merge-float {
    position: fixed;
    bottom: 15px;
    left: 20px;
    background-color: #bebebe9a;
    padding: 3px 8px 5px;
    border-radius: 3px;
  }
</style>

<script>
  import { mapState } from 'vuex'
  import router from '@/router'
  import tdColSafeHTML from '@/components/tdColSafeHTML'
  import tdColAction from '@/components/tdColAction'
  import PageTitle from '@/components/PageTitle.vue'
  import tdColUUID from '@/components/tdColUUID.vue'
  import DictionarySelectPI from '@/components/Dictionary/DictionarySelectPI.vue'
  import LegamDataTables from '@/components/LegamDataTables.vue'
  import CategorySelect from '@/components/CategorySelect.vue'
  import LegamDataTablesClientSide from '@/components/LegamDataTablesClientSide.vue'
  import SenseNewEdit from '@/components/SenseNewEdit.vue'
  import HelpStore from '@/store/helpstore.js'
  import config from '@/config.js'

  export default {
    name: 'SenseList',
    data(){
      return {
        dataColumnNames: ["id", "definition", "dictionaryAcronym", "fullCode"],
        columnsPI: [
          { title: this.$t('Definition'), field: 'definition', sortable: true, tdComp: tdColSafeHTML, searchable: true },
          { title: this.$t('Dictionary'), field: 'dictionaryAcronym', sortable: true, tdComp: tdColSafeHTML, searchable: true },
          { title: 'Category', field: 'fullCode', sortable: true, tdComp: tdColSafeHTML, searchable: true }
        ],
        columns: [
          { title: 'ID', field: 'id', sortable: true, tdComp: tdColUUID, searchable: true },
          { title: 'Definition', field: 'definition', sortable: true, tdComp: tdColSafeHTML, searchable: true },
          // { title: 'Paradefinition', field: 'paradefinition', sortable: true, tdComp: tdColSafeHTML, searchable: true },
          // { title: 'Designation', field: 'designation', sortable: true, tdComp: tdColSafeHTML, searchable: true },
          { title: 'Dictionary', field: 'dictionaryAcronym', sortable: true, tdComp: tdColSafeHTML, searchable: true },
          { title: 'Category', field: 'fullCode', sortable: true, tdComp: tdColSafeHTML, searchable: true },
          { title: 'Details', field: 'action', tdComp: tdColAction, thClass: 'tdAction',
            transformData: function(row){
              let retVal = [
                // {text: null, icon: 'pencil-fill', dataId: row.id},
                // {text: 'Details', icon: 'list-ul', modalTitle: 'Sense details: ' + row.lemmaForm, id: 'details-' + row.id},
                {text: null, icon: 'list-ul', id: row.id, to: {name: 'Sense', params: {id: row.id}}}
              ]
              // if (row.articleId == null){
              //   retVal.push(
              //     {text: 'Add to article', icon: 'plus-circle-fill', modalTitle: 'Add to article', id: row.id, to: {name: 'AddToArticle', id: row.id}}
              //   )
              // }
              return retVal
            }
          }
        ],
        url: '/senses/datatables',
        key: 1,
        searchableByColumn: true,
        config: config,
        selected: [],
        selectedCategories: [],
        mainSense: null,
        moveCategories: false,
      }
    },
    components: {
      PageTitle,
      LegamDataTables,
      LegamDataTablesClientSide,
      SenseNewEdit,
      DictionarySelectPI,
      CategorySelect
    },
    computed: {
      ...mapState({
        sense: state => state.sense.sense,
        userSettings: state => state.user.settings,
        userSettingsPI: state => state.user.settingsPI,
      }),
      tableAdditionalData(){
        return {
          'dictionaries': this.userSettings.dictionaries
        }
      },
    },
    watch: {
      "sense"(){
        this.$forceUpdate()
        this.$bvModal.show('modal-edit-sense')
      },
      "userSettings"(){
        this.key++
      },
      userSettingsPI: {
        deep: true,
        handler() {
          if (this.$refs.PITable.table) {
            this.$refs.PITable.sortPageFilter()
          }
        }
      },
    },
    methods: {
      sortedSelectedCategories() {
        let categoriesCopy = [...this.selectedCategories]
        return categoriesCopy.sort((a, b) => (a[2] > b[2]) ? 1 : -1)
      },
      cropSense(sense, maxChars = 18) {
        return sense.definition.length > maxChars ? sense.definition.slice(0, maxChars - 3) + '...' : sense.definition
      },
      tableAdditionalDataFilter(data) {
        if (this.userSettingsPI.dictionaries.length) {
          data = data.filter(row => this.userSettingsPI.dictionaries.includes(row.dictionaryAcronym))
        }
        return data
      },
      editSense(senseId){
        this.$store.dispatch('loadSense', {
            senseId: senseId
          })
      },
      openDetails(data) {
        router.push(`/sense/${data.id}`)
      },
      selectChanged(items){
        this.selected = items
        if (items.length){
          this.mainSense = items[0].id
        }
      },
      removeSelectedSense(sense){
        this.selected = this.selected.filter(_sense => _sense.id != sense.id)
      },
      removeSelectedCategory(category){
        this.selectedCategories = this.selectedCategories.filter(cat => cat[0] != category[0])
      },
      selectCategory(category) {
        this.selectedCategories.push([category.id, category.fullCode, category.concept])
      },
      manipulateCategories() {
        this.$store.dispatch('manipulateCategories', {
          senseIds: this.selected.map(sense => sense.id),
          categoryIds: this.selectedCategories.map(category => category[0])
        }).then(() => {
          this.$store.dispatch('addNotification', {
            type: 'success',
            text: 'category changes applied'
          })
          this.selected = []
          this.selectedCategories = []
          this.mainSense = null
          this.key++
        })
      },
      computeSelectedCategories() {
        this.$store.dispatch('loadSenseCategories', {
            senseIds: this.selected.map(sense => sense.id)
          }).then((value) => {
              this.selectedCategories = value.data.categories
          })
      },
      mergeSenses(){
        this.$store.dispatch('mergeSenses', {
          mainSenseId: this.mainSense,
          senseIds: this.selected.filter(sense => sense.id != this.mainSense).map(sense => sense.id),
          moveCategories: this.moveCategories
        }).then(() => {
          this.$store.dispatch('addNotification', {
            type: 'success',
            text: `Senses merged`
          })
          this.selected = []
          this.mainSense = null
          this.key++
        })
      },
      saveSense(){
        this.$store.dispatch('createSense', HelpStore.item).then((response) => {
          router.push(`/sense/${response.data.id}`)
        })
      }
    }
  }
</script>
